// Main-thread orchestrator for the on-device tools. Owns a single lazily-created
// worker (so neither the worker nor its heavy model libraries are fetched until
// the first run), prepares the source image, and resolves with the result PNG.

import type { LocalOpKind, LocalOpRequest, LocalOpResponse, LocalOpState } from './types';
import { isInstalled } from './install';

// Cap input dimensions before processing: bounds memory / output size for
// background removal, and keeps upscaling within reach on the GPU/CPU (a 2×
// upscale of a 1280px image is already 2560px). Mirrors capture.ts's approach.
const REMOVE_BG_MAX_INPUT = 2560;
const UPSCALE_MAX_INPUT = 1280;

// Where the background-removal model + wasm are served from. `undefined` uses
// the library's default (the imgly CDN). Set this to a self-hosted base URL
// (e.g. `new URL('assets/imgly/', document.baseURI).href`) to vendor the
// weights onto our own origin — the one place to flip CDN ↔ self-host.
const IMGLY_PUBLIC_PATH: string | undefined = undefined;

// Our self-hosted esrgan-slim x2 weights (copied by vite-plugin-static-copy —
// see vite.config.js). Resolved against the document so it works under the
// app's relative base in both dev and prod.
function upscaleModelUrl(): string {
  return new URL('assets/upscaler/esrgan-slim/x2/model.json', document.baseURI).href;
}

let worker: Worker | null = null;
let seq = 0;

function ensureWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL('./localOps.worker.ts', import.meta.url), { type: 'module' });
  }
  return worker;
}

// Terminate the worker to free the in-memory model (call when the studio
// closes). The next run re-creates it and reloads the model from cache.
export function disposeEngine(): void {
  if (worker) {
    worker.terminate();
    worker = null;
  }
}

function fit(w: number, h: number, max: number): { w: number; h: number } {
  if (w <= max && h <= max) return { w, h };
  const scale = Math.min(max / w, max / h);
  return { w: Math.round(w * scale), h: Math.round(h * scale) };
}

// Decode a data/blob URL and draw it (capped to `max`) onto a 2D canvas.
async function drawCapped(
  sourceUrl: string,
  max: number,
): Promise<{ canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D; w: number; h: number }> {
  const blob = await (await fetch(sourceUrl)).blob();
  const bitmap = await createImageBitmap(blob);
  const { w, h } = fit(bitmap.width, bitmap.height, max);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new Error('Canvas 2D context unavailable.');
  }
  // Draw before releasing the bitmap — closing it first detaches the source
  // and drawImage throws "The image source is detached".
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  return { canvas, ctx, w, h };
}

async function buildRequest(
  kind: LocalOpKind,
  sourceUrl: string,
  id: number,
): Promise<LocalOpRequest> {
  if (kind === 'upscale') {
    const { ctx, w, h } = await drawCapped(sourceUrl, UPSCALE_MAX_INPUT);
    const imageData = ctx.getImageData(0, 0, w, h);
    return {
      id,
      kind,
      image: { data: imageData.data.buffer as ArrayBuffer, width: w, height: h },
      modelUrl: upscaleModelUrl(),
    };
  }
  const { canvas } = await drawCapped(sourceUrl, REMOVE_BG_MAX_INPUT);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('Could not read the source image.');
  // Tell the worker whether the model was already fetched once, so it can label
  // cached fetches as "loading" rather than "downloading".
  return { id, kind, blob, publicPath: IMGLY_PUBLIC_PATH, installed: isInstalled('removeBg') };
}

export type RunCallbacks = { onProgress?: (state: Omit<LocalOpState, 'kind'>) => void };

// Run an on-device tool on a source image (data/blob URL) and resolve with the
// resulting PNG Blob. Progress is reported through onProgress.
export async function runLocalOp(
  kind: LocalOpKind,
  sourceUrl: string,
  { onProgress }: RunCallbacks = {},
): Promise<Blob> {
  const id = (seq += 1);
  const request = await buildRequest(kind, sourceUrl, id);
  const w = ensureWorker();

  return new Promise<Blob>((resolve, reject) => {
    const cleanup = () => {
      w.removeEventListener('message', onMessage);
      w.removeEventListener('error', onError);
    };
    const onMessage = (e: MessageEvent<LocalOpResponse>) => {
      const msg = e.data;
      if (!msg || msg.id !== id) return;
      if (msg.type === 'progress') {
        onProgress?.({ phase: msg.phase, progress: msg.progress });
      } else if (msg.type === 'done') {
        cleanup();
        resolve(msg.blob);
      } else {
        cleanup();
        reject(new Error(msg.message));
      }
    };
    const onError = (e: ErrorEvent) => {
      cleanup();
      reject(new Error(e.message || 'On-device tool failed.'));
    };
    w.addEventListener('message', onMessage);
    w.addEventListener('error', onError);

    if (request.kind === 'upscale') {
      // Transfer the pixel buffer to avoid a copy.
      w.postMessage(request, [request.image.data]);
    } else {
      w.postMessage(request);
    }
  });
}
